import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Cell, Legend, ReferenceLine } from 'recharts';
import { MapPinIcon, ChevronDownIcon, CalendarIcon, DollarSignIcon } from 'lucide-react';
import { Card } from '../../../components/ui/Card';
import { TabNavigation } from '../../../components/operations/TabNavigation';
import { OperationsSwitcher } from '../../../components/operations/OperationsSwitcher';
import { SummaryCard } from '../../../components/operations/costs/SummaryCard';
import { InsightCard, InsightItem } from '../../../components/operations/costs/InsightCard';
import { Button } from '../../../components/ui/Button';
const LOCATION_TABS = [{
  label: 'Overview',
  path: ''
}, {
  label: 'Capacity',
  path: 'capacity'
}, {
  label: 'Performance',
  path: 'performance'
}, {
  label: 'Costs',
  path: 'costs'
}, {
  label: 'Settings',
  path: 'settings'
}];
// Mock Data
const FIXED_COSTS = [{
  category: 'Rent',
  amount: 75000,
  pct: 51.7
}, {
  category: 'Utilities',
  amount: 28000,
  pct: 19.3
}, {
  category: 'Insurance',
  amount: 18000,
  pct: 12.4
}, {
  category: 'Software',
  amount: 12000,
  pct: 8.3
}, {
  category: 'Equip. Lease',
  amount: 8000,
  pct: 5.5
}, {
  category: 'Other',
  amount: 4000,
  pct: 2.8
}];
const VARIABLE_COSTS = [{
  category: 'Food Cost',
  amount: 85000,
  pct: 60.7
}, {
  category: 'Packaging',
  amount: 22000,
  pct: 15.7
}, {
  category: 'Platform Fees',
  amount: 18000,
  pct: 12.9
}, {
  category: 'Marketing',
  amount: 10000,
  pct: 7.1
}, {
  category: 'Maintenance',
  amount: 5000,
  pct: 3.6
}];
const COSTS_BY_LOCATION = [{
  name: 'Vračar',
  fixed: 45000,
  variable: 52000,
  total: 97000,
  revPct: 33.8,
  costOrd: 478,
  margin: 66.2,
  status: 'good'
}, {
  name: 'Banovo Brdo',
  fixed: 38000,
  variable: 35000,
  total: 73000,
  revPct: 35.2,
  costOrd: 527,
  margin: 64.8,
  status: 'good'
}, {
  name: 'Novi Beograd',
  fixed: 32000,
  variable: 28000,
  total: 60000,
  revPct: 34.1,
  costOrd: 508,
  margin: 65.9,
  status: 'good'
}, {
  name: 'SMASH Vračar',
  fixed: 18000,
  variable: 15000,
  total: 33000,
  revPct: 41.2,
  costOrd: 618,
  margin: 58.8,
  status: 'warning'
}, {
  name: 'SMASH NB',
  fixed: 12000,
  variable: 10000,
  total: 22000,
  revPct: 44.0,
  costOrd: 687,
  margin: 56.0,
  status: 'danger'
}];
const COST_TREND = [{
  month: 'Jan',
  fixed: 140000,
  variable: 120000,
  total: 260000
}, {
  month: 'Feb',
  fixed: 140000,
  variable: 125000,
  total: 265000
}, {
  month: 'Mar',
  fixed: 142000,
  variable: 128000,
  total: 270000
}, {
  month: 'Apr',
  fixed: 142000,
  variable: 130000,
  total: 272000
}, {
  month: 'May',
  fixed: 142000,
  variable: 135000,
  total: 277000
}, {
  month: 'Jun',
  fixed: 145000,
  variable: 138000,
  total: 283000
}, {
  month: 'Jul',
  fixed: 145000,
  variable: 140000,
  total: 285000
}, {
  month: 'Aug',
  fixed: 145000,
  variable: 135000,
  total: 280000
}, {
  month: 'Sep',
  fixed: 145000,
  variable: 138000,
  total: 283000
}, {
  month: 'Oct',
  fixed: 145000,
  variable: 142000,
  total: 287000
}, {
  month: 'Nov',
  fixed: 145000,
  variable: 145000,
  total: 290000
}, {
  month: 'Dec',
  fixed: 145000,
  variable: 140000,
  total: 285000
}];
const COST_PER_ORDER = [{
  name: 'Food Cost',
  value: 163,
  color: '#8B5CF6',
  pct: 10.9
}, {
  name: 'Labor',
  value: 153,
  color: '#7C3AED',
  pct: 10.3
}, {
  name: 'Rent',
  value: 143,
  color: '#6D28D9',
  pct: 9.6
}, {
  name: 'Packaging',
  value: 42,
  color: '#5B21B6',
  pct: 2.8
}, {
  name: 'Platform',
  value: 34,
  color: '#4C1D95',
  pct: 2.3
}, {
  name: 'Utilities',
  value: 27,
  color: '#A78BFA',
  pct: 1.8
}, {
  name: 'Other',
  value: 18,
  color: '#C4B5FD',
  pct: 1.2
}];
const PROFITABILITY = [{
  name: 'Vračar',
  revenue: 287000,
  cost: 97000,
  profit: 190000
}, {
  name: 'Banovo Brdo',
  revenue: 207000,
  cost: 73000,
  profit: 134000
}, {
  name: 'Novi Beograd',
  revenue: 176000,
  cost: 60000,
  profit: 116000
}, {
  name: 'SMASH Vračar',
  revenue: 80000,
  cost: 33000,
  profit: 47000
}, {
  name: 'SMASH NB',
  revenue: 50000,
  cost: 22000,
  profit: 28000
}];
const BREAK_EVEN = [{
  orders: 100,
  cost: 200000,
  revenue: 150000
}, {
  orders: 200,
  cost: 250000,
  revenue: 300000
}, {
  orders: 300,
  cost: 300000,
  revenue: 450000
}, {
  orders: 400,
  cost: 350000,
  revenue: 600000
}, {
  orders: 469,
  cost: 384500,
  revenue: 384500
}, {
  orders: 500,
  cost: 400000,
  revenue: 750000
}, {
  orders: 600,
  cost: 450000,
  revenue: 900000
}];
const BREAK_EVEN_TABLE = [{
  location: 'Vračar',
  target: 156,
  actual: 288,
  status: 'good',
  diff: '+85%'
}, {
  location: 'Banovo Brdo',
  target: 128,
  actual: 114,
  status: 'warning',
  diff: '-11%'
}, {
  location: 'Novi Beograd',
  target: 102,
  actual: 92,
  status: 'warning',
  diff: '-10%'
}, {
  location: 'SMASH Vračar',
  target: 48,
  actual: 53,
  status: 'good',
  diff: '+10%'
}, {
  location: 'SMASH NB',
  target: 35,
  actual: 32,
  status: 'warning',
  diff: '-9%'
}];
const INSIGHTS: InsightItem[] = [{
  id: '1',
  type: 'insight',
  icon: 'chart',
  text: 'Overall cost ratio improved to 36.5%',
  subtext: 'Down from 38.2% last month'
}, {
  id: '2',
  type: 'insight',
  icon: 'warning',
  text: 'SMASH NB profitability at risk',
  subtext: '56% margin vs 64% target'
}, {
  id: '3',
  type: 'insight',
  icon: 'check',
  text: 'Vračar is most profitable location',
  subtext: '66.2% margin, 190K profit'
}, {
  id: '4',
  type: 'insight',
  icon: 'trend-down',
  text: 'Platform fee ratio decreased',
  subtext: 'More Collina.rs direct orders'
}, {
  id: '5',
  type: 'insight',
  icon: 'trend-up',
  text: 'Packaging cost up 12%',
  subtext: 'Due to new supplier pricing'
}];
const RECOMMENDATIONS: InsightItem[] = [{
  id: '1',
  type: 'opportunity',
  priority: 'high',
  text: 'Negotiate rent at SMASH NB',
  subtext: '44% cost ratio is unsustainable'
}, {
  id: '2',
  type: 'opportunity',
  priority: 'high',
  text: 'Review COGS at Banovo Brdo',
  subtext: 'Food cost is 2% above average'
}, {
  id: '3',
  type: 'opportunity',
  priority: 'medium',
  text: 'Bundle utilities across locations',
  subtext: 'Potential 8% savings available'
}, {
  id: '4',
  type: 'opportunity',
  priority: 'medium',
  text: 'Increase Collina.rs orders',
  subtext: 'Save 30% on platform fees'
}, {
  id: '5',
  type: 'opportunity',
  priority: 'low',
  text: 'Renegotiate packaging contract',
  subtext: 'Volume discount available'
}];
const CustomTooltip = ({
  active,
  payload,
  label
}: any) => {
  if (active && payload && payload.length) {
    return <div className="bg-background-card border border-white/10 p-3 rounded-lg shadow-xl" data-id="element-1729">
        <p className="text-white font-bold mb-1" data-id="element-1730">{label}</p>
        {payload.map((entry: any, index: number) => <p key={index} style={{
        color: entry.color
      }} className="text-sm" data-id="element-1731">
            {entry.name}: {entry.value.toLocaleString()} {entry.unit || ''}
          </p>)}
      </div>;
  }
  return null;
};
export function Costs() {
  const [timeRange, setTimeRange] = useState('December 2025');
  const [locationFilter, setLocationFilter] = useState('All Locations');
  return <div className="space-y-6 animate-in fade-in duration-500 pb-10" data-id="element-1732">
      {/* Header & Navigation */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-1733">
        <h1 className="text-2xl font-bold text-text-primary" data-id="element-1734">Location Costs</h1>
        <div className="flex items-center gap-3" data-id="element-1735">
          <Button variant="outline" className="flex items-center gap-2" data-id="element-1736">
            <MapPinIcon className="w-4 h-4" data-id="element-1737" />
            {locationFilter}
            <ChevronDownIcon className="w-4 h-4 ml-1 opacity-50" data-id="element-1738" />
          </Button>
          <Button variant="outline" className="flex items-center gap-2" data-id="element-1739">
            <CalendarIcon className="w-4 h-4" data-id="element-1740" />
            {timeRange}
            <ChevronDownIcon className="w-4 h-4 ml-1 opacity-50" data-id="element-1741" />
          </Button>
        </div>
      </div>

      <OperationsSwitcher data-id="element-1742" />
      <TabNavigation tabs={LOCATION_TABS} basePath="/operations/locations" data-id="element-1743" />

      {/* Section 1: Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6" data-id="element-1744">
        <SummaryCard title="Total Monthly Cost" value="285,000 RSD" subtext="36.5% of Revenue" subtextColor="accent" delay={0.1} data-id="element-1745" />
        <SummaryCard title="Fixed Costs" value="145,000 RSD" subtext="18.6% of Revenue" delay={0.2} data-id="element-1746" />
        <SummaryCard title="Variable Costs" value="140,000 RSD" subtext="17.9% of Revenue" delay={0.3} data-id="element-1747" />
        <SummaryCard title="Cost Per Order" value="545 RSD" trend="3% vs last month" trendDirection="down" subtextColor="success" delay={0.4} data-id="element-1748" />
      </div>

      {/* Section 2: Fixed vs Variable Costs */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1749">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        delay: 0.5
      }} data-id="element-1750">
          <Card className="h-full" data-id="element-1751">
            <div className="flex justify-between items-center mb-4" data-id="element-1752">
              <h3 className="font-bold text-lg text-white" data-id="element-1753">Fixed Costs</h3>
              <span className="text-accent-primary font-bold" data-id="element-1754">145,000 RSD</span>
            </div>
            <div className="space-y-4" data-id="element-1755">
              {FIXED_COSTS.map((item, i) => <div key={i} data-id="element-1756">
                  <div className="flex justify-between text-sm mb-1" data-id="element-1757">
                    <span className="text-white" data-id="element-1758">{item.category}</span>
                    <span className="text-text-secondary" data-id="element-1759">
                      {item.amount.toLocaleString()} RSD
                    </span>
                  </div>
                  <div className="w-full bg-white/5 rounded-full h-2" data-id="element-1760">
                    <div className="bg-accent-primary h-2 rounded-full" style={{
                  width: `${item.pct}%`
                }} data-id="element-1761" />
                  </div>
                </div>)}
            </div>
          </Card>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        delay: 0.6
      }} data-id="element-1762">
          <Card className="h-full" data-id="element-1763">
            <div className="flex justify-between items-center mb-4" data-id="element-1764">
              <h3 className="font-bold text-lg text-white" data-id="element-1765">Variable Costs</h3>
              <span className="text-white font-bold" data-id="element-1766">140,000 RSD</span>
            </div>
            <div className="space-y-4" data-id="element-1767">
              {VARIABLE_COSTS.map((item, i) => <div key={i} data-id="element-1768">
                  <div className="flex justify-between text-sm mb-1" data-id="element-1769">
                    <span className="text-white" data-id="element-1770">{item.category}</span>
                    <span className="text-text-secondary" data-id="element-1771">
                      {item.amount.toLocaleString()} RSD
                    </span>
                  </div>
                  <div className="w-full bg-white/5 rounded-full h-2" data-id="element-1772">
                    <div className="bg-white h-2 rounded-full" style={{
                  width: `${item.pct}%`
                }} data-id="element-1773" />
                  </div>
                </div>)}
            </div>
          </Card>
        </motion.div>
      </div>

      {/* Section 3: Costs by Location */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.3,
      delay: 0.7
    }} data-id="element-1774">
        <Card noPadding className="overflow-hidden" data-id="element-1775">
          <div className="p-6 border-b border-white/5 flex justify-between items-center" data-id="element-1776">
            <h3 className="font-bold text-lg text-white" data-id="element-1777">Costs by Location</h3>
            <Button variant="ghost" size="sm" data-id="element-1778">
              Compare Mode ▼
            </Button>
          </div>
          <div className="overflow-x-auto" data-id="element-1779">
            <table className="w-full text-sm text-left" data-id="element-1780">
              <thead className="text-xs text-text-secondary uppercase bg-white/5" data-id="element-1781">
                <tr data-id="element-1782">
                  <th className="px-6 py-4 font-medium" data-id="element-1783">Location</th>
                  <th className="px-6 py-4 font-medium text-right" data-id="element-1784">Fixed</th>
                  <th className="px-6 py-4 font-medium text-right" data-id="element-1785">Variable</th>
                  <th className="px-6 py-4 font-medium text-right" data-id="element-1786">Total</th>
                  <th className="px-6 py-4 font-medium text-right" data-id="element-1787">% Rev</th>
                  <th className="px-6 py-4 font-medium text-right" data-id="element-1788">Cost/Ord</th>
                  <th className="px-6 py-4 font-medium text-right" data-id="element-1789">
                    Profit Margin
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5" data-id="element-1790">
                {COSTS_BY_LOCATION.map((loc, index) => <tr key={index} className="hover:bg-white/5 transition-colors" data-id="element-1791">
                    <td className="px-6 py-4 font-medium text-white" data-id="element-1792">
                      {loc.name}
                    </td>
                    <td className="px-6 py-4 text-right text-text-secondary" data-id="element-1793">
                      {loc.fixed.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 text-right text-text-secondary" data-id="element-1794">
                      {loc.variable.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 text-right text-white font-medium" data-id="element-1795">
                      {loc.total.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 text-right text-text-secondary" data-id="element-1796">
                      {loc.revPct}%
                    </td>
                    <td className="px-6 py-4 text-right text-white" data-id="element-1797">
                      {loc.costOrd} RSD
                    </td>
                    <td className="px-6 py-4 text-right" data-id="element-1798">
                      <div className="flex items-center justify-end gap-2" data-id="element-1799">
                        <div className="w-24 bg-white/5 rounded-full h-1.5" data-id="element-1800">
                          <div className={`h-1.5 rounded-full ${loc.status === 'danger' ? 'bg-red-500' : loc.status === 'warning' ? 'bg-yellow-500' : 'bg-accent-success'}`} style={{
                        width: `${loc.margin}%`
                      }} data-id="element-1801" />
                        </div>
                        <span className={`font-bold ${loc.status === 'danger' ? 'text-red-500' : loc.status === 'warning' ? 'text-yellow-500' : 'text-accent-success'}`} data-id="element-1802">
                          {loc.margin}% {loc.status === 'danger' && '⚠️'}
                        </span>
                      </div>
                    </td>
                  </tr>)}
                <tr className="bg-white/5 font-bold border-t border-white/10" data-id="element-1803">
                  <td className="px-6 py-4 text-white" data-id="element-1804">ALL LOCATIONS</td>
                  <td className="px-6 py-4 text-right text-white" data-id="element-1805">145,000</td>
                  <td className="px-6 py-4 text-right text-white" data-id="element-1806">140,000</td>
                  <td className="px-6 py-4 text-right text-accent-primary" data-id="element-1807">
                    285,000
                  </td>
                  <td className="px-6 py-4 text-right text-white" data-id="element-1808">36.5%</td>
                  <td className="px-6 py-4 text-right text-white" data-id="element-1809">545 RSD</td>
                  <td className="px-6 py-4 text-right text-accent-success" data-id="element-1810">
                    63.5%
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </Card>
      </motion.div>

      {/* Section 4: Cost Trend */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.3,
      delay: 0.8
    }} data-id="element-1811">
        <Card className="p-6" data-id="element-1812">
          <div className="flex justify-between items-center mb-6" data-id="element-1813">
            <h3 className="font-bold text-lg text-white" data-id="element-1814">Cost Trend</h3>
            <div className="flex items-center gap-4 text-xs" data-id="element-1815">
              <div className="flex items-center gap-2" data-id="element-1816">
                <span className="w-3 h-3 rounded-full bg-white" data-id="element-1817"></span>
                <span className="text-text-secondary" data-id="element-1818">Fixed</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-1819">
                <span className="w-3 h-3 rounded-full bg-accent-primary" data-id="element-1820"></span>
                <span className="text-text-secondary" data-id="element-1821">Variable</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-1822">
                <span className="w-3 h-3 rounded-full bg-accent-success" data-id="element-1823"></span>
                <span className="text-text-secondary" data-id="element-1824">Total</span>
              </div>
              <Button variant="ghost" size="sm" className="h-6 text-xs" data-id="element-1825">
                12M ▼
              </Button>
            </div>
          </div>
          <div className="h-[300px] w-full" data-id="element-1826">
            <ResponsiveContainer width="100%" height="100%" data-id="element-1827">
              <LineChart data={COST_TREND} data-id="element-1828">
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" vertical={false} data-id="element-1829" />
                <XAxis dataKey="month" stroke="#9CA3AF" tick={{
                fill: '#9CA3AF'
              }} axisLine={false} tickLine={false} data-id="element-1830" />
                <YAxis stroke="#9CA3AF" tick={{
                fill: '#9CA3AF'
              }} axisLine={false} tickLine={false} tickFormatter={value => `${value / 1000}K`} data-id="element-1831" />
                <Tooltip content={<CustomTooltip data-id="element-1833" />} data-id="element-1832" />
                <Line type="monotone" dataKey="total" stroke="#22C55E" strokeWidth={2} dot={false} name="Total Cost" unit=" RSD" data-id="element-1834" />
                <Line type="monotone" dataKey="fixed" stroke="#FFFFFF" strokeWidth={2} dot={false} name="Fixed Costs" unit=" RSD" data-id="element-1835" />
                <Line type="monotone" dataKey="variable" stroke="#8B5CF6" strokeWidth={2} dot={false} name="Variable Costs" unit=" RSD" data-id="element-1836" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </motion.div>

      {/* Section 5: Cost per Order */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.3,
      delay: 0.9
    }} data-id="element-1837">
        <Card className="p-6" data-id="element-1838">
          <div className="flex justify-between items-center mb-6" data-id="element-1839">
            <h3 className="font-bold text-lg text-white" data-id="element-1840">
              Cost per Order Breakdown
            </h3>
            <span className="text-text-secondary" data-id="element-1841">
              Average Order:{' '}
              <span className="text-white font-bold" data-id="element-1842">1,492 RSD</span>
            </span>
          </div>

          <div className="space-y-4" data-id="element-1843">
            {COST_PER_ORDER.map((item, i) => <div key={i} className="flex items-center gap-4" data-id="element-1844">
                <div className="w-32 text-sm text-text-secondary" data-id="element-1845">
                  {item.name}
                </div>
                <div className="flex-1 h-8 bg-white/5 rounded-r-md relative overflow-hidden" data-id="element-1846">
                  <motion.div initial={{
                width: 0
              }} animate={{
                width: `${item.pct * 3}%`
              }} // Multiplied for visual effect
              transition={{
                duration: 1,
                delay: 0.5 + i * 0.1
              }} className="h-full rounded-r-md" style={{
                backgroundColor: item.color
              }} data-id="element-1847" />
                </div>
                <div className="w-24 text-right font-bold text-white" data-id="element-1848">
                  {item.value} RSD
                </div>
                <div className="w-16 text-right text-sm text-text-secondary" data-id="element-1849">
                  {item.pct}%
                </div>
              </div>)}

            <div className="border-t border-white/10 pt-4 mt-4 flex items-center gap-4" data-id="element-1850">
              <div className="w-32 font-bold text-white" data-id="element-1851">PROFIT</div>
              <div className="flex-1 h-8 bg-white/5 rounded-r-md relative overflow-hidden" data-id="element-1852">
                <motion.div initial={{
                width: 0
              }} animate={{
                width: '61.1%'
              }} transition={{
                duration: 1,
                delay: 1.2
              }} className="h-full rounded-r-md bg-accent-success" data-id="element-1853" />
              </div>
              <div className="w-24 text-right font-bold text-accent-success" data-id="element-1854">
                912 RSD
              </div>
              <div className="w-16 text-right text-sm text-accent-success" data-id="element-1855">
                61.1%
              </div>
            </div>
          </div>
        </Card>
      </motion.div>

      {/* Section 6: Profitability Analysis */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.3,
      delay: 1.0
    }} data-id="element-1856">
        <Card className="p-6" data-id="element-1857">
          <h3 className="font-bold text-lg text-white mb-6" data-id="element-1858">
            Profitability by Location
          </h3>
          <div className="h-[300px] w-full" data-id="element-1859">
            <ResponsiveContainer width="100%" height="100%" data-id="element-1860">
              <BarChart data={PROFITABILITY} layout="vertical" margin={{
              top: 0,
              right: 30,
              left: 40,
              bottom: 0
            }} data-id="element-1861">
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="rgba(255,255,255,0.1)" data-id="element-1862" />
                <XAxis type="number" stroke="#9CA3AF" tick={{
                fill: '#9CA3AF'
              }} axisLine={false} tickLine={false} tickFormatter={value => `${value / 1000}K`} data-id="element-1863" />
                <YAxis dataKey="name" type="category" stroke="#9CA3AF" tick={{
                fill: '#9CA3AF'
              }} axisLine={false} tickLine={false} width={100} data-id="element-1864" />
                <Tooltip cursor={{
                fill: 'rgba(255,255,255,0.05)'
              }} content={<CustomTooltip data-id="element-1866" />} data-id="element-1865" />
                <Legend data-id="element-1867" />
                <Bar dataKey="revenue" name="Revenue" fill="#22C55E" radius={[0, 4, 4, 0]} barSize={20} data-id="element-1868" />
                <Bar dataKey="cost" name="Total Cost" fill="#EF4444" radius={[0, 4, 4, 0]} barSize={20} data-id="element-1869" />
                <Bar dataKey="profit" name="Profit" fill="#8B5CF6" radius={[0, 4, 4, 0]} barSize={20} data-id="element-1870" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </motion.div>

      {/* Section 7: Break-Even Analysis */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1871">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        delay: 1.1
      }} data-id="element-1872">
          <Card className="h-full p-6" data-id="element-1873">
            <h3 className="font-bold text-lg text-white mb-6" data-id="element-1874">
              Break-Even Analysis
            </h3>
            <div className="h-[300px] w-full" data-id="element-1875">
              <ResponsiveContainer width="100%" height="100%" data-id="element-1876">
                <LineChart data={BREAK_EVEN} data-id="element-1877">
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" data-id="element-1878" />
                  <XAxis dataKey="orders" stroke="#9CA3AF" label={{
                  value: 'Orders',
                  position: 'insideBottom',
                  offset: -5,
                  fill: '#9CA3AF'
                }} data-id="element-1879" />
                  <YAxis stroke="#9CA3AF" tickFormatter={value => `${value / 1000}K`} data-id="element-1880" />
                  <Tooltip content={<CustomTooltip data-id="element-1882" />} data-id="element-1881" />
                  <Line type="monotone" dataKey="revenue" stroke="#22C55E" strokeWidth={2} dot={false} name="Revenue" data-id="element-1883" />
                  <Line type="monotone" dataKey="cost" stroke="#EF4444" strokeWidth={2} dot={false} name="Cost" data-id="element-1884" />
                  <ReferenceLine x={469} stroke="white" strokeDasharray="3 3" label={{
                  value: 'Break-even: 469',
                  fill: 'white',
                  position: 'top'
                }} data-id="element-1885" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        delay: 1.2
      }} data-id="element-1886">
          <Card className="h-full flex flex-col" data-id="element-1887">
            <h3 className="font-bold text-lg text-white mb-6" data-id="element-1888">
              Break-Even Status
            </h3>
            <div className="overflow-x-auto" data-id="element-1889">
              <table className="w-full text-sm" data-id="element-1890">
                <thead data-id="element-1891">
                  <tr className="text-text-secondary border-b border-white/10" data-id="element-1892">
                    <th className="text-left pb-3 font-medium" data-id="element-1893">Location</th>
                    <th className="text-right pb-3 font-medium" data-id="element-1894">Break-Even</th>
                    <th className="text-right pb-3 font-medium" data-id="element-1895">Actual</th>
                    <th className="text-right pb-3 font-medium" data-id="element-1896">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5" data-id="element-1897">
                  {BREAK_EVEN_TABLE.map((item, i) => <tr key={i} data-id="element-1898">
                      <td className="py-4 font-medium text-white" data-id="element-1899">
                        {item.location}
                      </td>
                      <td className="py-4 text-right text-text-secondary" data-id="element-1900">
                        {item.target} ord
                      </td>
                      <td className="py-4 text-right text-white font-bold" data-id="element-1901">
                        {item.actual} ord
                      </td>
                      <td className={`py-4 text-right font-medium ${item.status === 'good' ? 'text-accent-success' : 'text-yellow-500'}`} data-id="element-1902">
                        {item.status === 'good' ? '✓' : '⚠️'} {item.diff}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
            <div className="mt-auto pt-4 text-center text-sm text-text-secondary" data-id="element-1903">
              2 of 5 locations below break-even point
            </div>
          </Card>
        </motion.div>
      </div>

      {/* Section 8: Insights */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1904">
        <InsightCard title="Cost Insights" items={INSIGHTS} variant="insights" delay={1.3} data-id="element-1905" />
        <InsightCard title="Cost Optimization" items={RECOMMENDATIONS} variant="opportunities" delay={1.4} data-id="element-1906" />
      </div>
    </div>;
}